import { selOrganizationUsers } from '@features/st-organizations/st-organization-module'
import { FolderListRow } from '@st/sdk'
import {
  Banner,
  Button,
  CheckIcon,
  Dialog,
  DialogButtons,
  EyeIcon,
  Modal,
  PencilIcon,
  Spinner,
  Tip,
  Wizard
} from '@st/theme'
import { inflect } from '@st/util/inflect'
import { clsx } from 'clsx'
import { useState } from 'react'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { selBulkSendSelectedFolders } from '../st-folder-list/st-folder-list-module'
import type {
  BulkSendClientOrganizersMessage,
  BulkSendClientOrganizersState
} from '../st-folder-list/st-bulk-send-client-organizers'
import { STEditTemplateInlineWizardPage } from './st-edit-template-inline-wizard-page'
import { openBulkEmailEditor } from '@features/st-folder-viewer/st-send-client-organizer-module'

type SendAction = { type: 'confirmSend' } | { type: 'confirmEnrollInReminders' }

export function STQuestionnairesBulkEmailPage({
  state,
  allFolders,
  send,
  emailsManagedBy,
  onClose,
  onClickEnrollInReminders
}: {
  state: BulkSendClientOrganizersState
  allFolders: FolderListRow[]
  send: (message: BulkSendClientOrganizersMessage) => void
  emailsManagedBy: 'stanfordtax' | 'karbon'
  onClose: () => void
  onClickEnrollInReminders: (folderIds: string[]) => void
}) {
  const stSendClientOrganizer = useProcess('stSendClientOrganizer')
  const organizationUsers = useSelect('stOrganization', selOrganizationUsers)

  const [action, setAction] = useState<SendAction | undefined>(undefined)

  function onClosePage() {
    onClose()
  }

  if (state.status == 'idle') {
    return (
      <Wizard.Scaffold
        onClose={onClosePage}
        bottomNav={
          <Wizard.BottomNav>
            <Button variant="default" onClick={onClosePage}>
              Back
            </Button>
          </Wizard.BottomNav>
        }
      >
        <Wizard.Main
          variant="wide"
          title="Send organizers"
          subtitle="No binders have been selected. Go back and select one or more binders to continue."
        >
          <div className="h-dvh" />
        </Wizard.Main>
      </Wizard.Scaffold>
    )
  }

  if (allFolders.length == 0) {
    return (
      <Wizard.Scaffold onClose={onClosePage}>
        <Wizard.Main variant="wide" title="Send organizers">
          <div className="flex h-dvh items-center justify-center">
            <Spinner className="size-8 fill-blue-500 text-blue-100" />
          </div>
        </Wizard.Main>
      </Wizard.Scaffold>
    )
  }

  const { folders, sendable, missingEmails } = selBulkSendSelectedFolders(
    state.folderIds,
    allFolders
  )

  function onClickSend() {
    if (folders.length > 0 && missingEmails.length == 0) {
      send({ type: 'send', sendableFolderIds: sendable.map((f) => f.id) })
    } else {
      setAction({ type: 'confirmSend' })
    }
  }

  if (state.status == 'editTemplate') {
    return (
      <STEditTemplateInlineWizardPage
        title="Send client organizers to clients"
        subtitle="Clients will receive the following email with a button to access their client organizer. The email will come from StanfordTax but it will be branded with your firm name and logo."
        banner={
          emailsManagedBy == 'karbon' && (
            <Banner variant="info">
              StanfordTax will send clients a direct link to their organizer. If you'd like to send
              a link to their Karbon for Clients Portal instead, you can do so via Karbon.
            </Banner>
          )
        }
        messageType="client_organizer_invitation"
        onClose={onClosePage}
        onClickBack={onClosePage}
        onClickContinue={() => send({ type: 'previewRecipients' })}
      />
    )
  }

  return (
    <Wizard.Scaffold
      onClose={onClosePage}
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={() => send({ type: 'editTemplate' })}>
            Back
          </Button>
          <div className="flex flex-row items-center gap-6">
            {match(state.status)
              .with('previewRecipients', () => (
                <Button variant="primary" onClick={onClickSend}>
                  Send {sendable.length} {inflect(sendable.length, 'client organizer')}
                </Button>
              ))
              .with('sending', () => (
                <Button variant="primary" disabled={true}>
                  Sending...
                </Button>
              ))
              .with('sent', () => {
                return (
                  <Button
                    variant="primary"
                    onClick={() => setAction({ type: 'confirmEnrollInReminders' })}
                    leadingIcon={<CheckIcon className="h-4 w-4" />}
                  >
                    Done
                  </Button>
                )
              })
              .run()}
          </div>
        </Wizard.BottomNav>
      }
    >
      <Wizard.Main
        variant="wide"
        title={state.status == 'sent' ? 'Sent organizers!' : 'Send organizers'}
        subtitle={
          state.status == 'sent'
            ? 'The organizers have successfully been emailed.'
            : `You're about to send organizers to ${sendable.length} ${inflect(
                sendable.length,
                'client'
              )}.`
        }
        banner={
          state.quotaExceeded != null ? (
            <Banner variant="error" className="sticky top-0 w-full rounded-none">
              You have exceeded the maximum quota of {state.quotaExceeded} emails for a free
              account. Please upgrade to remove this restriction.
            </Banner>
          ) : (
            <Banner variant="warning" className="sticky top-0 w-full rounded-none">
              Please review the emails below, including the salutations, before sending them.
            </Banner>
          )
        }
      >
        <STBulkSendTable
          folders={folders}
          onClickEdit={(folder) => {
            stSendClientOrganizer.send(openBulkEmailEditor(folder.id, organizationUsers))
          }}
        />
      </Wizard.Main>
      {match(action)
        .with({ type: 'confirmSend' }, () => {
          if (sendable.length == 0) {
            return (
              <Modal isOpen={true}>
                <Dialog
                  title="Missing emails"
                  subtitle="None of the selected clients have emails. In order to send the organizers, you must first add emails for those clients."
                  buttons={
                    <DialogButtons>
                      <Button variant="primary" onClick={() => setAction(undefined)}>
                        OK
                      </Button>
                    </DialogButtons>
                  }
                />
              </Modal>
            )
          }
          if (missingEmails.length > 0) {
            return (
              <Modal isOpen={true}>
                <Dialog
                  title="Missing emails"
                  subtitle={`An email is missing for ${missingEmails.length} ${inflect(
                    missingEmails.length,
                    'client'
                  )}. Would you like to send the organizers to the other ${inflect(
                    sendable.length,
                    'client'
                  )} to the other ${sendable.length} ${inflect(sendable.length, 'client')}?`}
                  buttons={
                    <DialogButtons>
                      <Button variant="default" onClick={() => setAction(undefined)}>
                        Go back
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          send({
                            type: 'send',
                            sendableFolderIds: sendable.map((f) => f.id)
                          })
                          setAction(undefined)
                        }}
                      >
                        Send to just {sendable.length}
                      </Button>
                    </DialogButtons>
                  }
                />
              </Modal>
            )
          }
        })
        .with({ type: 'confirmEnrollInReminders' }, () => {
          return (
            <Modal isOpen={true}>
              <Dialog
                title="Enroll clients in reminders?"
                buttons={
                  <DialogButtons>
                    <Button variant="default" onClick={onClosePage}>
                      No
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        const sentFolderIds = sendable.map((f) => f.id)
                        onClose()
                        onClickEnrollInReminders(sentFolderIds)
                      }}
                    >
                      Yes
                    </Button>
                  </DialogButtons>
                }
              >
                Would you like to enroll these clients in automated reminders?
              </Dialog>
            </Modal>
          )
        })
        .otherwise(() => null)}
    </Wizard.Scaffold>
  )
}

function STBulkSendTable({
  folders,
  onClickEdit
}: {
  folders: FolderListRow[]
  onClickEdit: (folder: FolderListRow) => void
}) {
  return (
    <table className="min-w-full table-fixed divide-y divide-gray-300">
      <thead>
        <tr className="h-9">
          <th scope="col" className="pl-8 text-left text-xs uppercase text-gray-600">
            Client
          </th>
          <th scope="col" className="pl-8 text-left text-xs uppercase text-gray-600">
            Salutation
          </th>
          <th scope="col" className="text-left text-xs uppercase text-gray-600">
            Email
          </th>
          <th scope="col" className="pr-8 text-left text-xs uppercase text-gray-600"></th>
          <th scope="col" className="pr-8 text-left text-xs uppercase text-gray-600"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {folders.map((folder) => {
          const clientEmails = folder.clientEmails

          return (
            <tr
              key={folder.id}
              className={clsx('group h-12', { 'bg-green-200': folder.clientOrganizer.sent })}
            >
              <td className="whitespace-nowrap pl-8">
                <div className="flex flex-row items-center gap-2">
                  <div className="text-base font-semibold text-gray-900">{folder.name}</div>
                </div>
              </td>
              <td className="whitespace-nowrap pl-8">
                <div className="flex flex-row items-center gap-2">
                  <div className="text-base font-semibold text-gray-900">{folder.salutation}</div>
                </div>
              </td>
              <td className="whitespace-nowrap text-base text-gray-500">
                <div className="flex flex-row items-center">
                  {clientEmails.join(', ')}

                  {clientEmails.length > 0 ? (
                    <div className="invisible ml-2 flex items-center group-hover:visible">
                      <Tip title="Edit emails">
                        <button onClick={() => onClickEdit(folder)}>
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </Tip>
                    </div>
                  ) : (
                    <Button variant="link" onClick={() => onClickEdit(folder)}>
                      Add email
                    </Button>
                  )}
                </div>
              </td>
              <td className="whitespace-nowrap text-base text-gray-500"></td>
              <td>
                <div className="flex flex-row items-center gap-4">
                  <button
                    className="flex flex-row items-center gap-2 text-sm text-gray-600"
                    onClick={() => onClickEdit(folder)}
                  >
                    <EyeIcon className="h-4 w-4" />
                    Preview email
                  </button>
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
